import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TextField, MenuItem, Popper, Tooltip } from '@material-ui/core';

import Utils from '@/common/utils/utils';
import {
    EXCLUDED_FIELDS,
    AUTH_METHOD_OPTIONS,
    AUTH_SETTING_FIELD_KEYS,
    openaiConnectorList,
    getFieldMeta,
    getFieldLabel
} from '../../handler/MCPServerUIHandler';
import { Autocomplete } from '@material-ui/lab';

import Constant from '@/common/constant/Constant';
import tr from '@/common/utils/tr';
import useTranslationSubscription from '@/hooks/useTranslation';


const getAuthMethodList = (mcpType) => {
    let authMethodList = [...AUTH_METHOD_OPTIONS];

    switch (mcpType) {
        case Constant.MCP_SERVER_TYPE.MCP_SERVER:
            authMethodList = authMethodList.map(auth => {
                auth.isdisable = false;
                if (['oauth2Setting', 'jwtSetting'].includes(auth.value)) {
                    auth.isdisable = true;
                }
                return auth;
            })
            break;
        // only on oauth2Setting
        case Constant.MCP_SERVER_TYPE.OPENAI_CONNECTOR:
            authMethodList = authMethodList.map(auth => {
                auth.isdisable = false;
                if (auth.value !== 'oauth2Setting') {
                    auth.isdisable = true;
                } 
                return auth;
            })
            break;
    
        default:
            break;
    }

    return authMethodList;
}

const getDefaultAuthMethodValue = (mcpType, fieldKey, fieldValue, onChange, authMethod) => {
    if (mcpType === Constant.MCP_SERVER_TYPE.OPENAI_CONNECTOR && fieldValue !== Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING) {
        onChange(fieldKey, Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING);
        return Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING;
    }

    if (mcpType === Constant.MCP_SERVER_TYPE.MCP_SERVER && fieldValue === Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING) {
        let newAuthMethod = Constant.AUTH_METHOD_MCP_SERVER.APIKEY_SETTING;
        if (authMethod !== Constant.AUTH_METHOD_MCP_SERVER.OAUTH2_SETTING) {
            newAuthMethod = authMethod;
        }
        onChange(fieldKey, newAuthMethod);
        return newAuthMethod;
    }



    return fieldValue;

}

const getScopeOptions = (selectConnectorId) => {
    let connectorObject = openaiConnectorList.find(conn => conn.value === selectConnectorId);
    if (!connectorObject) return [];
    return connectorObject.scopes;
};

const RenderAuthSection = memo(({ 
    fieldKey, 
    fieldValue, 
    errors, 
    authData = {}, 
    formData,
    onChange, 
    onBlur, 
    RenderField,
    initialData
}) => {
        useTranslationSubscription();
        
        const meta = getFieldMeta(fieldKey);
        const isRequired = meta.required || false;
        const errorMsg = errors[fieldKey] || '';


        const mcpType = formData?.type;
        const authMethodList = getAuthMethodList(mcpType);
        
        const defaultFieldValue = getDefaultAuthMethodValue(mcpType, fieldKey, fieldValue, onChange, initialData?.authMethod);
        const PopperComponentMemo = useMemo(() => {
            return ({ style, ...props }) => (
                <Popper {...props} style={{ ...style }} className={`selectDrop tag-field-multi-select`}  />
            );
        }, []);
        

        const renderNestedFields = useCallback(() => {
            const parentKey = fieldValue;
            const openaiConnector = formData?.openaiConnectorId;
             
            return (
                <div className="auth-section">
                    {Object.entries(authData).map(([key, value]) => {
                        if (EXCLUDED_FIELDS.includes(key)) return null;

                        if (key === 'redirectUrl') {
                            return (
                                <div className="api-field-wrapper">
                                    <h4 className="api-field-label">
                                        {getFieldLabel(key)}{': '}{`${process.env.REACT_APP_DOMAIN_NAME}oauth2callback`}
                                    </h4>
                                </div>
                            )
                        }
                        
                        if (key === 'scope') {
                            return (
                                <div key="auth-section">
                                <div className="api-field-wrapper">
                                    <h4 className="api-field-label">
                                        {getFieldLabel(key)}{' '}
                                        {<span style={{ color: 'red' }}>*</span>}
                                    </h4>
                                    <Autocomplete
                                        PopperComponent={PopperComponentMemo}
                                        multiple
                                        filterSelectedOptions
                                        sx={{
                                            maxHeight: 10
                                        }}
                                        options={getScopeOptions(openaiConnector)}
                                        getOptionLabel={option => option.label || ''}
                                        disableCloseOnSelect
                                        getOptionSelected={(option, selectedValue) =>
                                            option.label === selectedValue.label &&
                                            option.value === selectedValue.value
                                        }
                                        value={Array.isArray(value) ? value : []}
                                        // disabled={isDisableField}
                                        onChange={(e, newValue) => {
                                            onChange(parentKey, { ...authData, [key]: newValue });
                                        }}
                                        // onBlur={(e, newValue) => {
                                        //     onBlur(parentKey, { ...authData, [key]: newValue });
                                        // }}
                                        // error={!Utils.isEmptyString(errors[parentKey]?.[key])}
                                        // helperText={errors[parentKey]?.[key]}
                                        onFocus={e => {
                                            // onFocusHandler(e, {
                                            //     type: Constant.TAG_FIELD_OPTION_TYPE.SELECT
                                            // });
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                className="form-control form-control--wrapSelect form-control--hg40"
                                                error={!!errors[parentKey]?.[key]}
                                                helperText={''}
                                            />
                                        )}
                                        renderOption={option => (
                                            <Tooltip title={option.label}>
                                                <span
                                                    style={{
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {option.label}
                                                </span>
                                            </Tooltip>
                                        )}
                                        ListboxProps={{
                                            style: { maxHeight: 200, overflow: 'auto' } // Ensure scrolling
                                        }}
                                    />
                                    {errors[parentKey]?.[key] && (
                                        <div className="error-message" style={{ color: '#f44336', fontSize: '12px', marginTop: '4px',  fontWeight: 'normal' }}>
                                            {errors[parentKey][key]}
                                        </div>
                                    )}
                                </div>
                                </div>
                            );
                        }
                        return (
                            <RenderField
                                key={`${parentKey}.${key}`}
                                fieldKey={key}
                                fieldValue={value}
                                errorMsg={errors[parentKey]?.[key] || ''}
                                onChange={onChange}
                                onBlur={onBlur}
                                parentKey={parentKey}
                            />
                        );
                    })}
                </div>
            );
        }, [
            authData, fieldValue, onChange, onBlur, errors, RenderField
        ]);

        
        
        return (
            <div key="auth-section">
                <div className="api-field-wrapper">
                    <h4 className="api-field-label">
                        {getFieldLabel(fieldKey)}{' '}
                        {isRequired && <span style={{ color: 'red' }}>*</span>}
                    </h4>
                    <TextField
                        className="form-control-wrapInput api-field-input"
                        select
                        variant="outlined"
                        data-cy="auth-method-select"
                        value={defaultFieldValue}
                        error={!Utils.isEmptyString(errorMsg)}
                        helperText={errorMsg}
                        onChange={e =>  onChange(fieldKey, e.target.value)}
                        onBlur={e => onBlur(fieldKey, e.target.value, meta)}
                        SelectProps={{
                            MenuProps: {
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                getContentAnchorEl: null
                            },
                            onClose: e => {
                                onBlur(fieldKey, fieldValue || e.target.innerText, meta);
                            }
                        }}
                    >
                        {authMethodList.map((option) => (
                            <MenuItem 
                                data-cy="auth-method-item" 
                                key={option.label} 
                                value={option.value} 
                                disabled={option?.isdisable}
                            >
                                {tr(option.label)}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>

                {AUTH_SETTING_FIELD_KEYS.includes(fieldValue) && renderNestedFields()}
            </div>
        );
    }
);

RenderAuthSection.propTypes = {
    fieldKey: PropTypes.any,
    fieldValue: PropTypes.any,
    errors: PropTypes.any,
    authData: PropTypes.any,
    onChange: PropTypes.any,
    onBlur: PropTypes.any,
    handleRunAuth: PropTypes.any,
    RenderField: PropTypes.any,
    formData: PropTypes.any,
    initialData: PropTypes.any,
};

export default RenderAuthSection;


